import Link from 'next/link'
import Image from 'next/image'
import { eq } from 'drizzle-orm'
import { auth, signOut } from '@/auth'
import { db } from '@/db'
import { users } from '@/db/schema'
import BrandName from './BrandName'
import { SecondaryButton } from './FormFields'
import NavLinks from './NavLinks'
import ThemeToggle from './ThemeToggle'

export default async function Nav() {
  const session = await auth()

  let role: string | null = null
  let displayName: string | null = null
  let email: string | null = null
  if (session?.user?.id) {
    role = session.user.role ?? null
    const [user] = await db
      .select({ displayName: users.displayName, email: users.email })
      .from(users)
      .where(eq(users.id, session.user.id))
      .limit(1)
    displayName = user?.displayName ?? null
    email = user?.email ?? null
  }

  const isExpert = role === 'expert' || role === 'admin'
  const isAdmin = role === 'admin'
  const homeHref = session?.user?.id ? (isExpert ? '/inbox' : '/submit') : '/login'
  const userLabel = displayName ?? email ?? null
  const roleLabel = role === 'admin' ? 'Admin' : role === 'expert' ? 'Expert' : null
  const tenantName = session?.user?.tenantName ?? null
  const hasMultipleTenants = session?.user?.hasMultipleTenants ?? false

  return (
    <header className="sticky top-0 z-40 border-b border-zinc-200 bg-white/95 px-4 backdrop-blur dark:border-zinc-800 dark:bg-zinc-950/95">
      <div className="mx-auto max-w-6xl">

        {/*
          Mobile:  [Logo]──────────────[Controls]   ← row 1, justify-between
                   [────── Nav links centered ──────]  ← row 2, full-width
          Desktop: [Logo][──── Nav centered ────][Controls]  ← single row
        */}
        <div className="flex flex-wrap items-center justify-between gap-x-3 py-3">

          {/* Logo — always row 1, left */}
          <Link
            href={homeHref}
            className="flex items-center gap-2 text-sm font-semibold text-zinc-950 dark:text-white"
          >
            <Image src="/brand/qi-mark.png" alt="" width={28} height={28} priority className="h-7 w-7 rounded-md" />
            <BrandName />
          </Link>

          {/* Nav — row 2 (full-width, centered) on mobile; middle column on desktop */}
          {session?.user?.id ? (
            <div className="order-last flex w-full justify-center border-t border-zinc-100 pt-2 dark:border-zinc-800 sm:order-none sm:w-auto sm:flex-1 sm:border-0 sm:pt-0">
              <NavLinks isExpert={isExpert} isAdmin={isAdmin} />
            </div>
          ) : null}

          {/* Controls — always row 1, right */}
          <div className="flex items-center gap-3">
            <ThemeToggle />
            {hasMultipleTenants && tenantName ? (
              <Link
                href="/switch-tenant"
                className="hidden items-center gap-1.5 text-xs text-zinc-400 hover:text-zinc-700 dark:text-zinc-500 dark:hover:text-zinc-300 sm:flex"
                title="Switch tenant"
              >
                <span>{tenantName}</span>
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" fill="currentColor" className="h-3 w-3">
                  <path fillRule="evenodd" d="M5.22 10.22a.75.75 0 0 1 1.06 0L8 11.94l1.72-1.72a.75.75 0 1 1 1.06 1.06l-2.25 2.25a.75.75 0 0 1-1.06 0l-2.25-2.25a.75.75 0 0 1 0-1.06ZM10.78 5.78a.75.75 0 0 1-1.06 0L8 4.06 6.28 5.78a.75.75 0 0 1-1.06-1.06l2.25-2.25a.75.75 0 0 1 1.06 0l2.25 2.25a.75.75 0 0 1 0 1.06Z" clipRule="evenodd" />
                </svg>
              </Link>
            ) : null}
            {userLabel ? (
              <Link
                href="/settings"
                className="hidden items-center gap-2 text-xs text-zinc-500 hover:text-zinc-950 dark:text-zinc-400 dark:hover:text-zinc-100 sm:flex"
              >
                <span>{userLabel}</span>
                {roleLabel ? (
                  <span className="rounded-md border border-sky-200 bg-sky-50 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-sky-700 dark:border-sky-400/30 dark:bg-sky-400/10 dark:text-sky-300">
                    {roleLabel}
                  </span>
                ) : null}
              </Link>
            ) : null}
            {session?.user?.id ? (
              <form
                action={async () => {
                  'use server'
                  await signOut({ redirectTo: '/login' })
                }}
              >
                <SecondaryButton
                  type="submit"
                  className="px-3 py-1.5 text-sm"
                >
                  Sign out
                </SecondaryButton>
              </form>
            ) : null}
          </div>

        </div>
      </div>
    </header>
  )
}
